import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";

/**
 * 3D mouse-tilt card with shine sweep.
 * Wrap any element to get a perspective tilt + glare on hover.
 */
export const TiltCard = ({
    children,
    className = "",
    max = 10,
    glare = true,
    ...rest
}) => {
    const ref = useRef(null);
    const x = useMotionValue(0);
    const y = useMotionValue(0);
    const sx = useSpring(x, { stiffness: 180, damping: 18 });
    const sy = useSpring(y, { stiffness: 180, damping: 18 });
    const rotateX = useTransform(sy, [-0.5, 0.5], [max, -max]);
    const rotateY = useTransform(sx, [-0.5, 0.5], [-max, max]);
    const shineX = useTransform(sx, [-0.5, 0.5], ["20%", "80%"]);
    const shineY = useTransform(sy, [-0.5, 0.5], ["20%", "80%"]);
    const glareBg = useTransform(
        [shineX, shineY],
        ([sxv, syv]) =>
            `radial-gradient(circle at ${sxv} ${syv}, rgba(255,255,255,0.18), transparent 50%)`,
    );

    const onMove = (e) => {
        if (!ref.current) return;
        const rect = ref.current.getBoundingClientRect();
        const px = (e.clientX - rect.left) / rect.width - 0.5;
        const py = (e.clientY - rect.top) / rect.height - 0.5;
        x.set(px);
        y.set(py);
    };
    const onLeave = () => {
        x.set(0);
        y.set(0);
    };

    return (
        <motion.div
            ref={ref}
            onMouseMove={onMove}
            onMouseLeave={onLeave}
            style={{
                rotateX,
                rotateY,
                transformPerspective: 1000,
                transformStyle: "preserve-3d",
            }}
            className={`relative ${className}`}
            {...rest}
        >
            {children}
            {glare && (
                <motion.div
                    aria-hidden
                    className="pointer-events-none absolute inset-0 rounded-[inherit] mix-blend-overlay"
                    style={{
                        background: glareBg,
                    }}
                />
            )}
        </motion.div>
    );
};
